import { Component, OnInit, Input, computed, signal } from '@angular/core';
import type { EChartsOption } from 'echarts';
import { catchError, forkJoin, of } from 'rxjs';
import { IrrigationDataService } from './irrigation-data.service';
import { IrrigationData } from './irrigation-data.interface';
import { FieldDashboardDataService } from '../field-dashboard-data.service';
import { SyncService } from 'src/app/services/sync-service';
import { FieldIrrigationRate } from 'src/app/models/field-link';
import { FieldContext, WeatherStation } from 'src/app/models/field-context';
import { mmFor } from '../field-analysis/field-link-editor/irrigation-rate';
import { weatherSourceLabel } from './weather-source-label';

/** One tap's runs on one day, collapsed to a single bar. */
interface TapDayCell {
  date: string;
  objectName: string;
  objectId: number | null;
  /** Millimetres from this tap's wall-clock runtime that day, at the field's rate. */
  mm: number;
  /** Summed sumOfQtyGivenTime, in minutes. */
  minutes: number;
  /**
   * This tap's wall-clock minutes that day: the union of its own runs, so time it overlaps itself
   * counts once. The bar is drawn from it, and it decides whether the tap counts as having run.
   */
  runMinutes: number;
  earliestStartTime: string;
  runCount: number;
  /** The raw rows behind this bar, kept so the union can be taken over intervals, not totals. */
  runs: IrrigationData[];
}

/** A half-open [start, end) run interval in minutes from midnight on its own date. */
interface RunInterval {
  start: number;
  end: number;
}

@Component({
  selector: 'app-irrigation-chart',
  templateUrl: './irrigation-chart.component.html',
  styleUrls: ['./irrigation-chart.component.css'],
  standalone: false
})
export class IrrigationChartComponent implements OnInit {
  @Input() fieldId: string;
  chartOption: EChartsOption = {};
  summaryChartOption: EChartsOption = {};
  private subscription: any;

  // Deliberately excludes Precipitation's #5470c6 and Temperature's #ee6666, so a tap can never
  // be confused with a weather series now that the taps have a legend. Long enough that the worst
  // mapped field on staging (field 29, 14 taps) gets a distinct colour per tap.
  private static readonly TAP_COLOURS = [
    '#91cc75', '#fac858', '#73c0de', '#3ba272', '#fc8452', '#9a60b4', '#ea7ccc',
    '#4e79a7', '#59a14f', '#edc948', '#b07aa1', '#ff9da7', '#9c755f', '#bab0ac',
    '#8cd17d', '#f28e2b'
  ];
  private static readonly BAR_MAX_WIDTH = 8;
  // The day's highest temperature at the field's station (Morne, 28/10/2026), not the day's average.
  private static readonly TEMPERATURE_SERIES = 'Max temperature';
  private static readonly BAR_GAP = '10%';

  /**
   * What the sync service falls back to when neither the field nor its farm has a rate set, kept
   * the same as IrrigationRateService.DEFAULT_MM_PER_HOUR so an unreachable rate call does not
   * silently draw a different chart from the one the API would describe.
   */
  private static readonly DEFAULT_MM_PER_HOUR = 60 / 7.2;

  /**
   * A tap counts towards the day's average only when its own runs that day come to at least this
   * many minutes (Heinrich, 26/10/2026). Without it a sub-minute valve blip beside a three-hour
   * run halves the field's day: field 188 on staging, 08/07/2026, 0,51 and 180,52 minutes.
   */
  private static readonly MIN_RAN_MINUTES = 1;

  /**
   * Float slack on MIN_RAN_MINUTES. The union subtracts minutes-from-midnight, so 0,2 minutes at
   * 05:00 plus 0,8 at 13:00 comes out at 0,99999999999994, which is a full minute.
   */
  private static readonly MINUTES_EPSILON = 1e-6;

  /**
   * Field application rate, mm per hour of run time. A property of the field, not of one tap: at
   * 8,77 mm/h a hectare takes 87,7 m3/h, the flow into the field (Morne, 21/09/2026). It is no
   * longer a constant. Since PR 10105 a rate is set per farm with a per-field override, so it is
   * read per field and medianOfQtyGivenConvertedFromTime, which bakes in the old 8,333, is unused.
   */
  rateMmPerHour = IrrigationChartComponent.DEFAULT_MM_PER_HOUR;

  /** FIELD, FARM or DEFAULT, or null when the rate call did not answer. */
  rateSource: FieldIrrigationRate['source'] | null = null;

  /**
   * The station this field's weather is read from. Null when the sync service says the field's
   * tenant has none, undefined while that is not known: loading, a failed context call, or a sync
   * service that predates the field.
   */
  private station = signal<WeatherStation | null | undefined>(undefined);

  /** The line under the heading naming the weather source. Empty while the station is unknown. */
  weatherLabel = computed(() => weatherSourceLabel(this.station()));

  /**
   * The station from this field's own context. The data service outlives the page, so while a new
   * field's context loads the previous field's is still there, and it must not label this chart.
   */
  private static stationFor(data: any): WeatherStation | null | undefined {
    const context = data?.fieldContext as FieldContext | null | undefined;
    if (!context || data.fieldId == null || String(context.fieldId) !== String(data.fieldId)) {
      return undefined;
    }
    return context.weatherStation;
  }

  /**
   * Minutes from midnight for an API start time. Accepts "HH:mm" and "HH:mm:ss"; the fixtures use
   * the first and the API the second. Null for the weather-only rows, which carry no start time.
   */
  private static startMinutes(startTime: string | null): number | null {
    if (!startTime) {
      return null;
    }
    const parts = startTime.split(':');
    if (parts.length < 2) {
      return null;
    }
    const hours = Number(parts[0]);
    const minutes = Number(parts[1]);
    const seconds = parts.length > 2 ? Number(parts[2]) : 0;
    if (!Number.isFinite(hours) || !Number.isFinite(minutes) || !Number.isFinite(seconds)) {
      return null;
    }
    return hours * 60 + minutes + seconds / 60;
  }

  /**
   * Wall-clock minutes covered by these runs, counting overlapping time once. Called with one
   * tap's runs on one day: staging has taps whose own runs overlap (46 pairs in the 120 days to
   * 26/10/2026, up to 54,6 minutes on KDP-3215A on 03/08/2026), and that time is one tap's water,
   * not two. At a single run this is that run's duration, so it collapses to the summed duration
   * wherever nothing overlaps.
   *
   * Rows with no parseable start time still contribute their duration: dropping them would
   * under-count, and treating them as starting at midnight would overlap each other by accident.
   */
  private static unionMinutes(runs: IrrigationData[]): number {
    const intervals: RunInterval[] = [];
    let unplaced = 0;

    for (const row of runs) {
      const duration = row.sumOfQtyGivenTime;
      if (duration == null || !Number.isFinite(duration) || duration <= 0) {
        continue;
      }
      const start = IrrigationChartComponent.startMinutes(row.startTime);
      if (start == null) {
        unplaced += duration;
        continue;
      }
      intervals.push({ start, end: start + duration });
    }

    if (intervals.length === 0) {
      return unplaced;
    }

    intervals.sort((a, b) => a.start - b.start);
    let total = 0;
    let { start: openStart, end: openEnd } = intervals[0];

    for (let i = 1; i < intervals.length; i++) {
      const next = intervals[i];
      if (next.start <= openEnd) {
        // Overlapping or touching, so extend the open interval rather than counting it again.
        openEnd = Math.max(openEnd, next.end);
      } else {
        total += openEnd - openStart;
        openStart = next.start;
        openEnd = next.end;
      }
    }
    total += openEnd - openStart;

    return total + unplaced;
  }

  /**
   * The field's millimetres per date: the mean of the bars of the taps that ran that day, not their
   * sum or their union (Morne, 13/10/2026; Heinrich, 26/10/2026). Because it averages the bars, the
   * day figure is the mean of the bars the reader sees for that date, leaving out any blips.
   *
   * A tap under MIN_RAN_MINUTES is left out of the mean. When no tap reached it, the day falls back
   * to the mean of the taps with any runtime, so a field whose only water that day was a blip
   * keeps that water rather than reading zero beside a visible bar (126 of 10 555 staging
   * field-days in the 120 days to 26/10/2026, none over 0,98 minutes).
   */
  private static dayAverages(byTap: Map<string, Map<string, TapDayCell>>): Map<string, number> {
    const cellsByDate = new Map<string, TapDayCell[]>();
    byTap.forEach(byDate => byDate.forEach(cell => {
      if (cell.runMinutes <= 0) {
        return;
      }
      const cells = cellsByDate.get(cell.date) ?? [];
      cells.push(cell);
      cellsByDate.set(cell.date, cells);
    }));

    const threshold = IrrigationChartComponent.MIN_RAN_MINUTES - IrrigationChartComponent.MINUTES_EPSILON;
    const averages = new Map<string, number>();
    cellsByDate.forEach((cells, date) => {
      const ran = cells.filter(cell => cell.runMinutes >= threshold);
      const counted = ran.length > 0 ? ran : cells;
      averages.set(date, counted.reduce((sum, cell) => sum + cell.mm, 0) / counted.length);
    });
    return averages;
  }

  /**
   * Single place to change the tap label. object_name ("ELK-2108A") is the only human-readable
   * identifier and the one field_irrigation_mapping is keyed on, but it does not match the kraan
   * numbering used on site, so the final wording is still to be confirmed.
   */
  private tapLabel(objectName: string): string {
    return objectName;
  }

  constructor(
    private irrigationService: IrrigationDataService,
    private dataService: FieldDashboardDataService,
    private syncService: SyncService
  ) { }

  ngOnInit(): void {
    this.subscription = this.dataService.fieldData$.subscribe(data => {
      // Outside the date guard, so the label does not wait on a date range, and before the load,
      // whose summary reads it. The context arriving is itself an emission, so the chart is
      // redrawn with the station even when the stats came first.
      this.station.set(IrrigationChartComponent.stationFor(data));
      if (data.fieldId && data.startDate && data.endDate) {
        this.fieldId = data.fieldId;
        this.loadIrrigationData(data.startDate, data.endDate);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  loadIrrigationData(startDate: Date, endDate: Date): void {
    const fromDate = this.formatDateForApi(startDate);
    const toDate = this.formatDateForApi(endDate);

    // Both before drawing, so the chart is never rendered once at the default rate and again at the
    // field's. The rate call is allowed to fail on its own: it is limited to the tenant that owns
    // the farm, and a 403 there should cost the reader the rate, not the whole chart.
    forkJoin({
      stats: this.irrigationService.getIrrigationStats(fromDate, toDate, Number(this.fieldId)),
      rate: this.syncService.getFieldIrrigationRate(this.fieldId)
        .pipe(catchError(() => of(null as FieldIrrigationRate | null)))
    }).subscribe({
      next: ({ stats, rate }) => {
        this.rateMmPerHour = rate?.effectiveMmPerHour ?? IrrigationChartComponent.DEFAULT_MM_PER_HOUR;
        this.rateSource = rate?.source ?? null;
        this.updateChart(stats);
      },
      error: (error) => {
        console.error('Error fetching irrigation data:', error);
      }
    });
  }

  updateChart(data: IrrigationData[]): void {
    // Group data by date and sort by startTime. Keyed on the raw date from the API, not the
    // display label, which drops the year and would collide on ranges longer than 12 months.
    const dateGroups = data.reduce((acc, item) => {
      if (!acc[item.date]) {
        acc[item.date] = [];
      }
      acc[item.date].push(item);
      return acc;
    }, {} as { [key: string]: IrrigationData[] });

    // Sort runs within each date by startTime
    Object.keys(dateGroups).forEach(date => {
      dateGroups[date].sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));
    });

    const allDates = Object.keys(dateGroups).sort();
    // Rows with no tap identity are the weather-only rows the API adds for dates that have weather
    // but no irrigation. They must not become a series, or the legend grows a phantom entry.
    const irrigationRows = data.filter(d => d.objectName != null);

    // tap -> date -> that tap's runs on that date, collapsed into one bar. A tap can run several
    // times in a day, so its mm comes from the union of those runs at the field's rate. Usually
    // that is the summed duration; it is less only where the tap's own runs overlap.
    const byTap = new Map<string, Map<string, TapDayCell>>();
    for (const row of irrigationRows) {
      const tap = row.objectName as string;
      let byDate = byTap.get(tap);
      if (!byDate) {
        byDate = new Map<string, TapDayCell>();
        byTap.set(tap, byDate);
      }

      const cell = byDate.get(row.date);
      if (!cell) {
        byDate.set(row.date, {
          date: row.date,
          objectName: tap,
          objectId: row.objectId ?? null,
          mm: 0,
          minutes: row.sumOfQtyGivenTime ?? 0,
          runMinutes: 0,
          earliestStartTime: row.startTime,
          runCount: 1,
          runs: [row]
        });
      } else {
        cell.minutes += row.sumOfQtyGivenTime ?? 0;
        if ((row.startTime || '') < (cell.earliestStartTime || '')) {
          cell.earliestStartTime = row.startTime;
        }
        cell.runCount++;
        cell.runs.push(row);
      }
    }

    byTap.forEach(byDate => byDate.forEach(cell => {
      cell.runMinutes = IrrigationChartComponent.unionMinutes(cell.runs);
      cell.mm = mmFor(cell.runMinutes, this.rateMmPerHour);
    }));
    const dayAvgByDate = IrrigationChartComponent.dayAverages(byTap);

    // Sorted by name, not by first run time, so a tap's colour depends only on which taps are
    // present and not on which of them happened to irrigate first in the range.
    const tapNames = [...byTap.keys()].sort((a, b) => a.localeCompare(b));

    // At four taps or fewer hold an explicit category gap for the look that was asked for. Above
    // that let ECharts use its own adaptive gap, which narrows as the column count grows: a fixed
    // 30% wastes band width that 15 columns cannot spare.
    const categoryGap = tapNames.length <= 4 ? { barCategoryGap: '30%' } : {};

    const series: any[] = tapNames.map((tap, i) => {
      const byDate = byTap.get(tap)!;
      return {
        name: this.tapLabel(tap),
        type: 'bar',
        yAxisIndex: 0,
        // No `stack`, so each tap takes its own column slot and the bars sit side by side.
        // barMaxWidth rather than barWidth: ECharts does not clamp an explicit barWidth to the
        // band width, so a fixed 8px overlaps into neighbouring days on a field with many taps.
        barMaxWidth: IrrigationChartComponent.BAR_MAX_WIDTH,
        barGap: IrrigationChartComponent.BAR_GAP,
        ...categoryGap,
        itemStyle: {
          color: IrrigationChartComponent.TAP_COLOURS[i % IrrigationChartComponent.TAP_COLOURS.length]
        },
        // One point per date in the range, null on the days this tap did not run. The nulls are
        // what keep the tap's colour and its legend entry stable across the whole range.
        data: allDates.map(date => {
          const cell = byDate.get(date);
          return cell ? { value: cell.mm, itemData: cell } : null;
        })
      };
    });

    this.chartOption = {
      ...this.chartOption,
      grid: {
        left: '10%',
        right: '10%',
        top: '70px',
        bottom: '60px',
        containLabel: true
      },
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => {
          // Branch on data shape before series name: a tap is identified by carrying itemData, so
          // a tap named after a weather series cannot hijack that branch.
          const item = params.data?.itemData as TapDayCell | undefined;
          if (item) {
            // The field's figure for the day: the mean of the taps that ran, not their sum.
            const dayAvg = dayAvgByDate.get(item.date) ?? 0;
            return `
              <strong>Tap:</strong> ${this.tapLabel(item.objectName)}<br/>
              <strong>Date:</strong> ${item.date}<br/>
              <strong>First Start:</strong> ${item.earliestStartTime}<br/>
              <strong>Irrigation:</strong> ${item.mm.toFixed(2)} mm${item.runCount > 1 ? ` (${item.runCount} runs)` : ''}<br/>
              <strong>Duration:</strong> ${item.minutes} min<br/>
              <strong>Day Avg (taps that ran):</strong> ${dayAvg.toFixed(2)} mm
            `;
          }

          const seriesName = params.seriesName;
          if (seriesName === IrrigationChartComponent.TEMPERATURE_SERIES) {
            return `<strong>Date:</strong> ${params.name}<br/><strong>Max temperature:</strong> ${params.value != null ? params.value.toFixed(1) + '°C' : 'N/A'}`;
          }
          if (seriesName === 'ET0') {
            return `<strong>Date:</strong> ${params.name}<br/><strong>ET0:</strong> ${params.value != null ? params.value.toFixed(2) + ' mm' : 'N/A'}`;
          }
          if (seriesName === 'Precipitation') {
            return `<strong>Date:</strong> ${params.name}<br/><strong>Precipitation:</strong> ${params.value != null ? params.value.toFixed(1) + 'mm' : 'N/A'}`;
          }
          return '';
        }
      },
      legend: {
        // Scrolling because a mis-mapped field can carry 14 taps, which would otherwise wrap over
        // the plot area.
        type: 'scroll',
        top: 0,
        data: [...tapNames.map(tap => this.tapLabel(tap)), IrrigationChartComponent.TEMPERATURE_SERIES, 'ET0', 'Precipitation']
      },
      xAxis: {
        type: 'category',
        data: allDates.map(date => this.formatDate(date))
      },
      yAxis: [
        {
          type: 'value',
          // ET0 and precipitation were moved onto this axis on 31/08/2026 because they are also
          // in mm, but the label still said "Irrigation (mm)". Kept short deliberately: the name
          // is rotated 90 degrees so its length eats chart height in a narrow column.
          // Precipitation shares this axis too and is named in the legend rather than here.
          name: 'Irrigation & ET0 (mm)',
          nameLocation: 'middle',
          nameGap: 50,
          nameRotate: 90
        },
        {
          type: 'value',
          name: 'Max temp (°C)',
          nameLocation: 'middle',
          nameGap: 50,
          nameRotate: -90,
          position: 'right'
        }
      ],
      series: [
        ...series,
        {
          name: IrrigationChartComponent.TEMPERATURE_SERIES,
          type: 'line',
          yAxisIndex: 1,
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: { color: '#ee6666', width: 2 },
          itemStyle: { color: '#ee6666' },
          data: allDates.map(date => {
            const runs = dateGroups[date];
            const temp = runs.find(r => r.avgHcAirTemperature != null)?.avgHcAirTemperature;
            return temp ?? null;
          })
        },
        {
          name: 'ET0',
          type: 'line',
          smooth: true,
          symbol: 'diamond',
          symbolSize: 6,
          lineStyle: { color: '#73c0de', width: 2 },
          itemStyle: { color: '#73c0de' },
          data: allDates.map(date => {
            const runs = dateGroups[date];
            const et0 = runs.find(r => r.sumEt0 != null)?.sumEt0;
            return et0 ?? null;
          })
        },
        {
          name: 'Precipitation',
          type: 'bar',
          barMaxWidth: 8,
          itemStyle: { color: '#5470c6', opacity: 0.5 },
          data: allDates.map(date => {
            const runs = dateGroups[date];
            const precip = runs.find(r => r.sumPrecipitation != null)?.sumPrecipitation;
            return precip ?? null;
          })
        }
      ]
    };

    // Summary chart - ET0 vs Total Irrigation as percentage.
    // The API stamps the day's weather onto every run row, so ET0 has to be counted once per
    // date. Totalling over rows multiplies it by the number of runs that day.
    const et0ByDate = new Map<string, number>();
    data.forEach(d => {
      if (d.sumEt0 != null && !et0ByDate.has(d.date)) {
        et0ByDate.set(d.date, d.sumEt0);
      }
    });
    const totalEt0 = [...et0ByDate.values()].reduce((sum, v) => sum + v, 0);
    // Irrigation is per date too: the range total is the sum of the daily averages over the taps
    // that ran, so % of ET0 compares one field-day of water with one day of ET0. Totalling the
    // rows straight multiplied the range by the number of taps on the field.
    const totalIrrigation = [...dayAvgByDate.values()].reduce((sum, v) => sum + v, 0);
    const irrigationPercent = totalEt0 > 0 ? (totalIrrigation / totalEt0) * 100 : 0;

    // Rain is stamped onto every run row exactly as ET0 is, so it is counted once per date for the
    // same reason. It is deliberately not folded into irrigationPercent: that figure means
    // irrigation against ET0 and people have already made decisions from it.
    const rainByDate = new Map<string, number>();
    data.forEach(d => {
      if (d.sumPrecipitation != null && !rainByDate.has(d.date)) {
        rainByDate.set(d.date, d.sumPrecipitation);
      }
    });
    const totalRain = [...rainByDate.values()].reduce((sum, v) => sum + v, 0);

    const hasRain = totalRain > 0;
    const mmLabel = (p: any) => `${p.value.toFixed(2)} mm`;

    // The sync service said the field has no weather station, so there is no ET0 to add up. The ET0
    // row says so rather than "0.00 mm", which reads as a measurement. Only on a known null: while
    // the station is unknown the row keeps its figure, as it did before the station was sent.
    const noStation = this.station() === null;
    const et0RowText = (p: any) => (noStation ? 'no weather station' : mmLabel(p));

    // Rain against ET0, asked for separately from the irrigation figure (Morne, 07/10/2026). Its
    // own percentage, not folded into irrigationPercent, which still means irrigation alone.
    const rainPercent = totalEt0 > 0 ? (totalRain / totalEt0) * 100 : 0;

    // Where the row's figures go. A right-positioned label needs clear space to its right, and
    // there is none when the stack is the longest thing on the chart: the text runs off the edge of
    // the grid. So above half the axis everything goes inside the blue segment, which is wide by
    // definition, and below it everything goes to the right, where the short stack leaves room.
    // Either way it is one label, so the two figures cannot be laid on top of each other.
    const axisMax = Math.max(totalEt0, totalIrrigation + totalRain);
    const labelInside = axisMax > 0 && totalIrrigation / axisMax >= 0.5;

    // With no ET0 for the range a percentage is undefined, not zero. "0.0% of ET0" reads as a
    // measurement, so the clause is dropped rather than shown as a figure nobody can act on.
    const pctSuffix = (percent: number) => (totalEt0 > 0 ? ` (${percent.toFixed(1)}% of ET0)` : '');
    const irrigationText = `${totalIrrigation.toFixed(2)} mm${pctSuffix(irrigationPercent)}`;

    // With rain, each part carries its own figure on its own segment and the row ends with
    // irrigation plus rain (Heinrich, 28/10/2026). "% of ET0" is spelled out once, on the total.
    // Text width is estimated, not measured: about 6 px a character on a plot about 700 px wide
    // (staging, 28/10/2026), so a character takes about 0,9 % of the axis. A segment too narrow for
    // its figure hands it to the total at the end of the row, so no figure goes missing.
    const CHAR_SHARE = 0.009;
    const stackTotal = totalIrrigation + totalRain;
    const shortPct = (value: number) => (totalEt0 > 0 ? ` (${((value / totalEt0) * 100).toFixed(1)}%)` : '');
    const irrigationShort = `${totalIrrigation.toFixed(2)} mm${shortPct(totalIrrigation)}`;
    const rainShort = `${totalRain.toFixed(2)} mm rain${shortPct(totalRain)}`;
    const totalFigure = `${stackTotal.toFixed(2)} mm${pctSuffix(totalEt0 > 0 ? (stackTotal / totalEt0) * 100 : 0)}`;
    const layoutAt = (xMax: number) => {
      const irrigationFits = xMax > 0 && totalIrrigation / xMax >= irrigationShort.length * CHAR_SHARE;
      const rainFits = xMax > 0 && totalRain / xMax >= rainShort.length * CHAR_SHARE;
      const handedOn = [irrigationFits ? '' : irrigationShort, rainFits ? '' : rainShort].filter(t => t);
      const totalText = handedOn.length ? `${handedOn.join(' + ')} = ${totalFigure}` : `Total ${totalFigure}`;
      return { irrigationFits, rainFits, totalText };
    };
    // The axis runs past the stack by the total label's width, so the label at the end of the row
    // always has room. Which figures fit is decided once, at the room the short "Total" label needs.
    // A figure handed on lengthens that label and widens the axis again, but the decision stands:
    // deciding again at the wider axis shrank the segments and pushed out a rain figure that fitted.
    const roomFor = (text: string) => Math.max(axisMax, stackTotal / Math.max(0.2, 1 - text.length * CHAR_SHARE));
    let xMax = axisMax;
    let rainLayout = layoutAt(xMax);
    if (hasRain && axisMax > 0) {
      xMax = roomFor(`Total ${totalFigure}`);
      rainLayout = layoutAt(xMax);
      xMax = roomFor(rainLayout.totalText);
    }

    this.summaryChartOption = {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: any) => {
          if (!Array.isArray(params)) return '';
          // Rain carries a null on the ET0 row, which must not render as a "0.00 mm" line.
          // Total only places the end-of-row label; it is not a figure of its own.
          return params
            .filter((p: any) => p.value != null && p.seriesName !== 'Total')
            .map((p: any) => {
              const label = p.seriesName === 'Rain' ? 'Sum of Rain' : p.name;
              const value = p.seriesName !== 'Rain' && p.dataIndex === 0 ? et0RowText(p) : mmLabel(p);
              return `<strong>${label}:</strong> ${value}`;
            })
            .join('<br/>');
        }
      },
      grid: { left: '25%', right: '15%', top: '10px', bottom: '10px' },
      xAxis: hasRain ? { type: 'value', show: false, max: xMax } : { type: 'value', show: false },
      yAxis: {
        type: 'category',
        data: ['Sum of ET0', 'Sum of Irrigation']
      },
      series: [{
        name: 'Summary',
        type: 'bar',
        stack: 'summary',
        data: [
          // The ET0 row is unchanged: nothing stacks onto it, its label stays on the right.
          { value: totalEt0, itemStyle: { color: '#ee6666' } },
          {
            value: totalIrrigation,
            itemStyle: { color: '#5470c6' },
            // With rain: irrigation's own figure inside its segment, or handed to the total when the
            // segment is too narrow. Without rain: the one figure for the row, inside when the bar is
            // the longest thing on the chart (outside it would run off the grid), else to the right.
            label: hasRain
              ? (rainLayout.irrigationFits ? { show: true, position: 'inside', color: '#fff' } : { show: false })
              : (labelInside ? { position: 'inside', color: '#fff' } : {})
          }
        ],
        label: {
          show: true,
          position: 'right',
          formatter: (p: any) => (p.dataIndex === 1 ? (hasRain ? irrigationShort : irrigationText) : et0RowText(p))
        }
      }, {
        name: 'Rain',
        type: 'bar',
        stack: 'summary',
        // Null on the ET0 row, so rain extends the irrigation bar only. Null again when no rain
        // fell, so the range does not grow a "0.00 mm rain" that reads as a measurement. Same
        // colour and opacity as the Precipitation bars above, so rain reads the same in both.
        data: [
          null,
          hasRain
            ? {
                value: totalRain,
                itemStyle: { color: '#5470c6', opacity: 0.5 },
                // Dark text: the half-opacity segment is too light for white.
                label: rainLayout.rainFits ? { show: true, position: 'inside', color: '#1f2a44' } : { show: false }
              }
            : null
        ],
        label: {
          show: true,
          position: 'inside',
          formatter: (p: any) => (p.value == null ? '' : rainShort)
        }
      }, ...(hasRain ? [{
        // Not a figure: a sliver at the end of the stack that carries the row's total to its right.
        name: 'Total',
        type: 'bar' as const,
        stack: 'summary',
        silent: true,
        data: [null, { value: xMax * 0.0001, itemStyle: { color: 'transparent' } }],
        label: { show: true, position: 'right' as const, formatter: () => rainLayout.totalText }
      }] : [])]
    };
  }

  formatDateForApi(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    return `${day} ${month}`;
  }
}
